import PropTypes from "prop-types";
import * as React from "react";
import {
  OTHER_PRODUCT_TYPE,
  autographTypeOptions,
  groupedTypeMatches,
} from "../../../domain/productComposer/composer";
import {
  Alert,
  Autocomplete,
  Radio,
  SectionHead,
  Typography,
} from "../../../themeV2";
import css from "./create.module.css";

const PRESETS = [
  {
    preset: "autograph",
    label: "Autograph",
    description: "A signed item: a print, poster or Funko Pop.",
  },
  {
    preset: "videoShoutout",
    label: "Video shoutout",
    description: "A personalized video message.",
  },
  {
    preset: "audioShoutout",
    label: "Audio shoutout",
    description: "A personalized audio message.",
  },
  {
    preset: "advanced",
    label: "Advanced",
    description: "Any product type, with no defaults.",
    adminOnly: true,
  },
];

const PRINT_SIZES = /8"x10"|11"x17"/;

const typeValue = (productTypeId) =>
  productTypeId === null ? "other" : String(productTypeId);

const typeFromValue = (value) => (value === "other" ? null : Number(value));

const AdvancedTypePicker = function AdvancedTypePicker({
  productTypes,
  selected,
  onSelect,
}) {
  const [inputValue, setInputValue] = React.useState(selected?.name ?? "");
  const groups = groupedTypeMatches({ productTypes, query: inputValue });

  return (
    <Autocomplete.Root
      label="Product type"
      placeholder="Search product types"
      value={selected ?? null}
      inputValue={inputValue}
      onInputValueChange={setInputValue}
      onValueChange={(productType) => productType && onSelect(productType)}
      autoComplete="none"
      itemToStringLabel={(productType) => productType?.name ?? ""}
    >
      {[...groups, { groupName: "", productTypes: [OTHER_PRODUCT_TYPE] }].map(
        (group) => (
          <Autocomplete.Group key={group.groupName || "other"}>
            {group.groupName && (
              <Autocomplete.GroupLabel>
                {group.groupName}
              </Autocomplete.GroupLabel>
            )}
            {group.productTypes.map((productType) => (
              <Autocomplete.Item
                key={typeValue(productType.productTypeId)}
                value={productType}
              >
                {productType.name}
              </Autocomplete.Item>
            ))}
          </Autocomplete.Group>
        ),
      )}
    </Autocomplete.Root>
  );
};

AdvancedTypePicker.propTypes = {
  onSelect: PropTypes.func.isRequired,
  productTypes: PropTypes.arrayOf(PropTypes.object).isRequired,
  selected: PropTypes.object,
};

const PresetPage = function PresetPage({
  state,
  productTypes,
  shop,
  isAdmin,
  onChoosePreset,
  onChooseType,
  showErrors,
}) {
  const selectedType =
    state.productTypeId === null
      ? OTHER_PRODUCT_TYPE
      : productTypes.find((t) => t.productTypeId === state.productTypeId);
  const internationalDefault = productTypes.find(
    (t) => t.isDefaultInternational,
  );
  const showInternationalWarning =
    !shop.isDomestic &&
    PRINT_SIZES.test(selectedType?.name ?? "") &&
    internationalDefault &&
    internationalDefault.productTypeId !== state.productTypeId;

  return (
    <div className={css.page}>
      <section className={css.section}>
        <SectionHead title="What are you selling?" as="h3" />
        <Radio.Group
          value={state.preset ?? ""}
          onValueChange={onChoosePreset}
          className={css.presets}
        >
          {PRESETS.filter((p) => isAdmin || !p.adminOnly).map((p) => (
            <Radio.Item
              key={p.preset}
              className={css.preset}
              value={p.preset}
              label={p.label}
              description={p.description}
            />
          ))}
        </Radio.Group>
      </section>

      {state.preset === "autograph" && (
        <section className={css.section}>
          <SectionHead title="Product type" as="h3" />
          <Radio.Group
            value={typeValue(state.productTypeId)}
            onValueChange={(value) => onChooseType(typeFromValue(value))}
          >
            {autographTypeOptions(productTypes).map((productType) => (
              <Radio.Item
                key={typeValue(productType.productTypeId)}
                value={typeValue(productType.productTypeId)}
                label={productType.name}
              />
            ))}
          </Radio.Group>
          {state.productTypeId === null && (
            <Typography variant="body-md" color="variant" as="p">
              Other is for anything not listed. Our team reviews these and sets
              up the right product type.
            </Typography>
          )}
        </section>
      )}

      {state.preset === "advanced" && (
        <section className={css.section}>
          <SectionHead title="Product type" as="h3" />
          <AdvancedTypePicker
            productTypes={productTypes}
            selected={selectedType}
            onSelect={(productType) => onChooseType(productType.productTypeId)}
          />
        </section>
      )}

      {showErrors && !(state.preset && state.productTypeId !== undefined) && (
        <Typography variant="body-md" color="error" as="p" role="alert">
          Choose what you&apos;re selling and its product type.
        </Typography>
      )}

      {showInternationalWarning && (
        <Alert severity="warning" title="Check the product type">
          International shops usually sell {internationalDefault.name}.{" "}
          {selectedType.name} may cost more to ship internationally.
        </Alert>
      )}
    </div>
  );
};

PresetPage.propTypes = {
  isAdmin: PropTypes.bool,
  onChoosePreset: PropTypes.func.isRequired,
  onChooseType: PropTypes.func.isRequired,
  productTypes: PropTypes.arrayOf(PropTypes.object).isRequired,
  shop: PropTypes.shape({ isDomestic: PropTypes.bool }).isRequired,
  showErrors: PropTypes.bool,
  state: PropTypes.object.isRequired,
};

export default PresetPage;
